export type AccentColor =
	| "teal"
	| "orange"
	| "violet"
	| "pink"
	| "amber"
	| "blue"
	| "emerald"
	| "rose";

export type MapVisibility = "private" | "unlisted" | "public";
export type LayerKind = "standard" | "linked" | "forked";
export type GeomType = "point" | "line" | "polygon";
export type AccessRole = "viewer" | "editor";
export type ResourceType = "map" | "layer";
export type PrincipalType = "user" | "token" | "email";
export type SubmissionStatus = "pending" | "approved" | "rejected";
export type BaseStyle = "osm" | "dark" | "satellite";

export type BBox = [number, number, number, number];

export interface ViewState {
	center: [number, number];
	zoom: number;
	bbox?: BBox;
}

export interface LayerStyle {
	color?: string;
	width?: number;
	opacity?: number;
	icon?: string;
	label?: boolean;
}

export interface GeoJsonGeometry {
	type: string;
	coordinates: unknown;
}

export interface MapSummary {
	id: string;
	ownerId: string;
	title: string;
	description: string | null;
	visibility: MapVisibility;
	baseStyle: BaseStyle;
	viewState: ViewState | null;
	createdAt: string;
	updatedAt: string;
}

export interface Layer {
	id: string;
	mapId: string;
	title: string;
	kind: LayerKind;
	sourceLayerId: string | null;
	sourceMapId: string | null;
	style: LayerStyle | null;
	defaultVisible: boolean;
	zIndex: number;
	createdAt: string;
	updatedAt: string;
}

export interface Entry {
	id: string;
	layerId: string;
	geomType: GeomType;
	geometry: GeoJsonGeometry;
	bbox: BBox;
	title: string;
	description: string | null;
	properties: Record<string, unknown> | null;
	mediaKeys: string[] | null;
	createdAt: string;
	updatedAt: string;
}

export interface AccessGrant {
	id: string;
	resourceType: ResourceType;
	resourceId: string;
	principalType: PrincipalType;
	principalId: string;
	role: AccessRole;
	token: string | null;
	expiresAt: string | null;
	revokedAt: string | null;
	createdAt: string;
}

export interface Submission {
	id: string;
	mapId: string;
	layerId: string | null;
	geomType: GeomType;
	geometry: GeoJsonGeometry;
	title: string;
	description: string | null;
	submitterEmail: string | null;
	status: SubmissionStatus;
	decisionUserId: string | null;
	rejectReason: string | null;
	createdAt: string;
	updatedAt: string;
}

export interface ApiErrorBody {
	error: {
		message: string;
		code: string;
		details?: unknown;
	};
}

export type CreateMapInput = {
	title: string;
	description?: string;
	visibility?: MapVisibility;
	baseStyle?: BaseStyle;
	viewState?: ViewState;
};

export type UpdateMapInput = Partial<CreateMapInput>;

export type CreateLayerInput = {
	title: string;
	style?: LayerStyle;
	defaultVisible?: boolean;
	zIndex?: number;
};

export type UpdateLayerInput = Partial<CreateLayerInput> & {
	kind?: LayerKind;
};

export type CreateEntryInput = {
	geomType: GeomType;
	geometry: GeoJsonGeometry;
	title: string;
	description?: string;
	properties?: Record<string, unknown>;
};

export type UpdateEntryInput = Partial<CreateEntryInput> & {
	layerId?: string;
};

export type CreateShareInput = {
	role: AccessRole;
};

export type EffectiveAccessRole = "owner" | AccessRole;

export type ImportMode = "fork" | "link";

export type ImportLayerInput = {
	sourceLayerId?: string;
	sourceMapId?: string;
	mode: ImportMode;
	style?: LayerStyle;
};

export function computeBbox(geometry: GeoJsonGeometry): BBox {
	const coords: number[][] = [];
	collectCoords(geometry.coordinates, coords);
	if (coords.length === 0) {
		return [0, 0, 0, 0];
	}
	let minLng = Infinity;
	let minLat = Infinity;
	let maxLng = -Infinity;
	let maxLat = -Infinity;
	for (const [lng, lat] of coords) {
		if (typeof lng !== "number" || typeof lat !== "number") continue;
		if (lng < minLng) minLng = lng;
		if (lat < minLat) minLat = lat;
		if (lng > maxLng) maxLng = lng;
		if (lat > maxLat) maxLat = lat;
	}
	return [minLng, minLat, maxLng, maxLat];
}

function collectCoords(node: unknown, out: number[][]): void {
	if (!Array.isArray(node)) return;
	if (node.length >= 2 && typeof node[0] === "number" && typeof node[1] === "number") {
		out.push([node[0], node[1]]);
		return;
	}
	for (const child of node) {
		collectCoords(child, out);
	}
}

export function isValidGeomType(value: string): value is GeomType {
	return value === "point" || value === "line" || value === "polygon";
}

export function geomTypeFromGeoJson(type: string): GeomType | null {
	switch (type) {
		case "Point":
			return "point";
		case "LineString":
		case "MultiLineString":
			return "line";
		case "Polygon":
		case "MultiPolygon":
			return "polygon";
		default:
			return null;
	}
}

function isPosition(value: unknown): value is [number, number] {
	return (
		Array.isArray(value) &&
		value.length >= 2 &&
		typeof value[0] === "number" &&
		typeof value[1] === "number" &&
		Number.isFinite(value[0]) &&
		Number.isFinite(value[1]) &&
		value[0] >= -180 &&
		value[0] <= 180 &&
		value[1] >= -90 &&
		value[1] <= 90
	);
}

function isLineCoords(value: unknown, minPoints: number): boolean {
	return Array.isArray(value) && value.length >= minPoints && value.every(isPosition);
}

function isPolygonCoords(value: unknown): boolean {
	if (!Array.isArray(value) || value.length === 0) return false;
	for (const ring of value) {
		if (!isLineCoords(ring, 4)) return false;
		const first = ring[0] as [number, number];
		const last = ring[ring.length - 1] as [number, number];
		if (first[0] !== last[0] || first[1] !== last[1]) return false;
	}
	return true;
}

export type GeometryValidation =
	| { ok: true; geometry: GeoJsonGeometry }
	| { ok: false; message: string };

export function validateGeometry(
	geomType: GeomType,
	geometry: GeoJsonGeometry | null | undefined,
): GeometryValidation {
	if (!geometry || typeof geometry !== "object" || !geometry.type) {
		return { ok: false, message: "Geometry is required" };
	}

	const expected = geomTypeFromGeoJson(geometry.type);
	if (expected !== geomType) {
		return {
			ok: false,
			message: `Geometry type ${geometry.type} does not match geomType ${geomType}`,
		};
	}

	const coords = geometry.coordinates;
	switch (geometry.type) {
		case "Point":
			if (!isPosition(coords)) {
				return { ok: false, message: "Point requires [lng, lat] coordinates" };
			}
			break;
		case "LineString":
			if (!isLineCoords(coords, 2)) {
				return { ok: false, message: "LineString requires at least 2 positions" };
			}
			break;
		case "MultiLineString":
			if (!Array.isArray(coords) || coords.length === 0 || !coords.every((line) => isLineCoords(line, 2))) {
				return { ok: false, message: "MultiLineString requires non-empty line arrays" };
			}
			break;
		case "Polygon":
			if (!isPolygonCoords(coords)) {
				return {
					ok: false,
					message: "Polygon requires closed rings with at least 4 positions",
				};
			}
			break;
		case "MultiPolygon":
			if (
				!Array.isArray(coords) ||
				coords.length === 0 ||
				!coords.every((poly) => isPolygonCoords(poly))
			) {
				return { ok: false, message: "MultiPolygon requires valid polygon rings" };
			}
			break;
		default:
			return { ok: false, message: `Unsupported geometry type ${geometry.type}` };
	}

	return { ok: true, geometry };
}

export function parseBboxQuery(raw: string | null | undefined): BBox | null {
	if (!raw) return null;
	const parts = raw.split(",").map((p) => Number(p.trim()));
	if (parts.length !== 4 || parts.some((n) => !Number.isFinite(n))) return null;
	const [minLng, minLat, maxLng, maxLat] = parts;
	if (minLng > maxLng || minLat > maxLat) return null;
	return [minLng, minLat, maxLng, maxLat];
}

export function bboxIntersects(a: BBox, b: BBox): boolean {
	return a[0] <= b[2] && a[2] >= b[0] && a[1] <= b[3] && a[3] >= b[1];
}

export function unionBboxes(boxes: BBox[]): BBox | null {
	if (boxes.length === 0) return null;
	let minLng = Infinity;
	let minLat = Infinity;
	let maxLng = -Infinity;
	let maxLat = -Infinity;
	let any = false;
	for (const box of boxes) {
		if (!box || box.length !== 4) continue;
		const [west, south, east, north] = box;
		if (
			![west, south, east, north].every((n) => typeof n === "number" && Number.isFinite(n))
		) {
			continue;
		}
		any = true;
		if (west < minLng) minLng = west;
		if (south < minLat) minLat = south;
		if (east > maxLng) maxLng = east;
		if (north > maxLat) maxLat = north;
	}
	if (!any) return null;
	return [minLng, minLat, maxLng, maxLat];
}

export function isDegenerateBbox(bbox: BBox): boolean {
	return bbox[0] === bbox[2] && bbox[1] === bbox[3];
}

export function boundsFromEntries(entries: { bbox: BBox }[]): BBox | null {
	return unionBboxes(entries.map((e) => e.bbox));
}

export const LAYER_ACCENT_COLORS = [
	"teal",
	"orange",
	"violet",
	"pink",
	"amber",
	"blue",
	"emerald",
	"rose",
] as const satisfies readonly AccentColor[];

export const ACCENT_HEX: Record<AccentColor, string> = {
	teal: "#14b8a6",
	orange: "#f97316",
	violet: "#8b5cf6",
	pink: "#ec4899",
	amber: "#f59e0b",
	blue: "#3b82f6",
	emerald: "#10b981",
	rose: "#f43f5e",
};

export function resolveLayerColor(style: LayerStyle | null | undefined, fallbackIndex = 0): string {
	const color = style?.color;
	if (color && color in ACCENT_HEX) {
		return ACCENT_HEX[color as AccentColor];
	}
	if (color && /^#[0-9a-fA-F]{6}$/.test(color)) {
		return color;
	}
	const accent = LAYER_ACCENT_COLORS[fallbackIndex % LAYER_ACCENT_COLORS.length];
	return ACCENT_HEX[accent];
}

export * from "./geojsonImport";
